import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { catchError, debounceTime, firstValueFrom, map, of, take } from 'rxjs';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { GalaxyResizeModule } from '@vendasta/galaxy/resize';
import { BREAKPOINTS_PX } from '@vendasta/galaxy/constants';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateService } from '@ngx-translate/core';
import { BuilderComponent } from '../builder/builder.component';
import { PreviewComponent } from '../preview/preview.component';
import { SendPreviewDialogComponent } from '../send-preview-dialog/send-preview-dialog.component';
import {
  Contact,
  ContentData,
  CONTENT_BUILDER_DEPENDENCIES,
  DEFAULT_CONTENT_DATA,
  PreviewResult,
  SendPreviewDialogData,
} from '../interface';

export const PREVIEW_DEBOUNCE_MS = 300;

@Component({
  selector: 'email-builder-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressBarModule, MatTabsModule, GalaxyResizeModule, BuilderComponent, PreviewComponent],
})
export class PageComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly contentBuilderDependencies = inject(CONTENT_BUILDER_DEPENDENCIES);
  private readonly isCreateMode = toSignal(
    inject(ActivatedRoute, { optional: true })?.data.pipe(map((data) => data['mode'] === 'create')) ?? of(false),
    { initialValue: false },
  );
  private readonly dialog = inject(MatDialog);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly snackbar = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  protected readonly contactProvider = this.contentBuilderDependencies?.contactProvider;
  private readonly unsavedChangesService = this.contentBuilderDependencies?.unsavedChangesService;
  protected readonly saving = signal(false);
  protected readonly isValid = signal(true);

  protected readonly isMobile = toSignal(
    this.breakpointObserver
      .observe([`(max-width: ${BREAKPOINTS_PX.TABLET_MINIMUM})`])
      .pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  protected readonly initialContentData = signal<ContentData>(null);
  protected readonly currentContentData = signal<ContentData>(null);
  protected readonly selectedContact = signal<Contact | null>(null);
  private readonly debouncedContentData = toSignal(
    toObservable(this.currentContentData).pipe(debounceTime(PREVIEW_DEBOUNCE_MS)),
    {
      initialValue: null,
    },
  );

  protected readonly hasUnsavedChanges = computed(() => {
    if (this.isCreateMode() && !this.saving()) return true;
    const initial = this.initialContentData();
    const current = this.currentContentData();
    if (!initial || !current) return false;
    return JSON.stringify(initial) !== JSON.stringify(current);
  });

  protected readonly preview = rxResource({
    params: () => ({ data: this.debouncedContentData(), contact: this.selectedContact() }),
    stream: ({ params: { data, contact } }) => {
      if (!data) {
        return of(null);
      }
      return this.contentBuilderDependencies.builderService
        .generatePreview(data, contact)
        .pipe(catchError(() => of(null)));
    },
  });

  protected readonly previewHtml = linkedSignal<PreviewResult | undefined, string>({
    source: this.preview.value,
    computation: (value, previous) => value?.html ?? previous?.value ?? '',
  });

  protected readonly previewSubject = linkedSignal<PreviewResult | undefined, string>({
    source: this.preview.value,
    computation: (value, previous) => value?.subject ?? previous?.value ?? this.currentContentData()?.subjectLine ?? '',
  });

  protected readonly previewPreviewText = linkedSignal<PreviewResult | undefined, string>({
    source: this.preview.value,
    computation: (value, previous) =>
      value?.previewText ?? previous?.value ?? this.currentContentData()?.previewText ?? '',
  });

  constructor() {
    this.contentBuilderDependencies.builderService
      .getBuilderContent()
      .pipe(take(1))
    
      .subscribe((data) => {
        const content = data ?? this.contentBuilderDependencies.defaultContent ?? DEFAULT_CONTENT_DATA;
        this.initialContentData.set(content);
        this.currentContentData.set(content);
      });

    effect(() => this.unsavedChangesService?.setUnsavedChanges(this.hasUnsavedChanges()));
    inject(DestroyRef).onDestroy(() => this.unsavedChangesService?.setUnsavedChanges(false));
  }

  protected onContentDataChange(data: ContentData): void {
    const current = this.currentContentData();
    if (current && JSON.stringify(current) === JSON.stringify(data)) {
      return;
    }
    this.currentContentData.set(data);
  }

  protected onValidChange(valid: boolean): void {
    this.isValid.set(valid);
  }

  protected async onSaveClicked(): Promise<void> {
    const contentData = this.currentContentData();
    if (!contentData) {
      return;
    }
    const service = this.contentBuilderDependencies.builderService;
    if (!service.saveContent) {
      return;
    }
    this.saving.set(true);
    try {
      await firstValueFrom(service.saveContent(contentData).pipe(take(1)));
      this.initialContentData.set(contentData);
      this.snackbar.openSuccessSnack(this.translate.instant('CONTENT_BUILDER.SAVE_SUCCESS'));
    } catch {
      this.snackbar.openErrorSnack(this.translate.instant('CONTENT_BUILDER.SAVE_ERROR'));
    } finally {
      this.saving.set(false);
    }
  }

  protected onSendPreviewClicked(): void {
    const contactProvider = this.contentBuilderDependencies.contactProvider;
    if (!contactProvider) {
      return;
    }

    const contentData = this.currentContentData();
    if (!contentData?.id) {
      return;
    }

    const sendPreviewService = this.contentBuilderDependencies.sendPreviewService;
    if (!sendPreviewService) {
      return;
    }

    const contentId = contentData.id;

    const dialogData: SendPreviewDialogData = {
      hasUnsavedChanges: this.hasUnsavedChanges(),
      contactProvider,
      onSend: async (contact: Contact) => {
        await firstValueFrom(sendPreviewService.sendPreview(contentId, contact.id).pipe(take(1)));
        this.snackbar.openSuccessSnack('CONTENT_BUILDER.SEND_PREVIEW_SUCCESS');
      },
    };

    this.dialog.open(SendPreviewDialogComponent, {
      data: dialogData,
      viewContainerRef: this.viewContainerRef,
    });
  }
}
